import { type ButtonHTMLAttributes, type ReactNode } from "react";
import { Link } from "react-router";
import cn from "classnames";
import OnboardingTooltip from "../OnboardingTooltip";
import "./Button.css";

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  children?: ReactNode;
  variant?: "primary" | "secondary" | "tertiary" | "plain" | "danger" | "destructive" | "dashed";
  size?: "sm" | "md" | "lg" | "huge";
  className?: string;
  isIconButton?: boolean;
  link?: string;
  textDuringDisabled?: string;
  fullWidthTooltip?: boolean;
}

const sizeTypographyMap: Record<NonNullable<Props["size"]>, string> = {
  sm: "text-label-xs",
  md: "text-label-sm",
  lg: "text-label-md",
  huge: "text-label-lg",
};

export default function Button({
  children = "Button",
  variant = "primary",
  size = "huge",
  type = "button",
  isIconButton = false,
  className,
  link,
  disabled,
  onClick,
  textDuringDisabled,
  fullWidthTooltip = false,
  ...rest
}: Props) {
  const showTooltip = Boolean(disabled && textDuringDisabled);

  const buttonClassName = cn(
    "btn",
    `btn-${variant}`,
    `btn-${size}`,
    !isIconButton && sizeTypographyMap[size],
    className,
    {
      "btn-icon": isIconButton,
      "btn-base": !isIconButton,
      "w-full": showTooltip && fullWidthTooltip,
    },
  );

  let element: ReactNode;
  if (link) {
    element = (
      <Link
        to={disabled ? "#" : link}
        className={buttonClassName}
        aria-disabled={disabled}
        onClick={(e) => {
          if (disabled) {
            e.preventDefault();
            return;
          }
          onClick?.(e as any);
        }}>
        {children}
      </Link>
    );
  } else {
    element = (
      <button type={type} disabled={disabled} onClick={onClick} className={buttonClassName} {...rest}>
        {children}
      </button>
    );
  }

  if (!showTooltip) {
    return element;
  }

  return (
    <OnboardingTooltip
      className={cn("cursor-not-allowed", fullWidthTooltip ? "flex w-full" : "inline-flex")}
      content={
        <div className="max-w-[220px] text-body-regular-xs content-on-background-neutral">{textDuringDisabled}</div>
      }>
      {element}
    </OnboardingTooltip>
  );
}
